import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Checkbox, Typography, Breadcrumbs } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import translate from "../../../../i18n/translate";
import "./reactTable.scss";
import DialogView from "../DialogContents";
import {
  getVocabularyContents,
  vocabularyDeleteWordList,
  vocabularyUpdateWordList,
} from "../../../../redux/actions/vocabularyCenterAction";
import { USER } from "../../../../redux/constants";
import TableComponent from "./TableComponent";
import CreateAudioComponent from "./createAudioComponent";
import { getRandomNumber } from "../../../shared/utils/sharedUtils";
import { addWordToList, handleCheckedOptions, isDeletable } from "../VocabularyCenterPOCUtils";
import { useNavigate } from "react-router-dom";
import {
  AccessTimeFilledIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  InfoIcon,
  NavigateNextIcon,
  TuneIcon,
} from "../../../common/icon";

export interface CheckOption {
  name: string;
  checked: boolean;
  id: number;
}
export const getStandardHeaderView = (text: any, closeFn: any) => {
  return (
    <div className="standard-view-header-box">
      <Typography className="standard-view-text">{text}</Typography>
      <CloseIcon className="standard-view-close-icon" onClick={closeFn} />
    </div>
  );
};

export const convertToObject = (optionsArray: CheckOption[]) => {
  const result: Record<string, boolean> = {};
  for (const option of optionsArray) {
    result[option.id] = option.checked;
  }
  return result;
};

const ReactTable = ({
  setInfoDetails,
  isUserWordList,
  showingWordListDetails,
  setShowWords,
  changeView,
  openAddWordDialog,
  setAddNewWord,
  breadcrumbTitle,
  mainView,
  searchText,
}: any) => {
  const navigate = useNavigate();
  const WORDLIST_REGEX = /^[ A-Za-z0-9()-.;:?!&]*$/;
  const dispatch = useDispatch();
  const reduxState = useSelector((state: any) => state);
  const [topicLevelWords, setTopicLevelWords] = React.useState<any>([]);
  const [tableBodyContents, setTableBodyContents] = React.useState<any>([]);
  const [showNoWordsMessage, setShowNoWordsMessage] = React.useState<boolean>(false);
  const [showOptions, setShowOptions] = React.useState<boolean>(false);
  const [showEdit, setShowEdit] = React.useState<boolean>(false);
  const [editableWordList, setEditableWordList] = React.useState<any>("");
  const [showDelete, setShowDelete] = React.useState<boolean>(false);
  const [showError, setShowError] = React.useState<boolean>(false);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = React.useState<boolean>(false);
  const [isMobileWidth, setIsMobileWidth] = React.useState<boolean>(false);
  const forceUpdate = React.useReducer(() => ({}), {})[1];
  const [filteredTopic, setFilteredTopic] = useState<any>([]);

  const checkOptions = [
    {
      name: "Word",
      checked: true,
      id: 1,
    },
    {
      name: "Translation",
      checked: false,
      id: 2,
    },
    {
      name: "Parts of Speech",
      checked: true,
      id: 3,
    },
    {
      name: "Definition",
      checked: true,
      id: 4,
    },
    {
      name: "Sentence",
      checked: true,
      id: 5,
    },
  ];
  const [options] = React.useState(checkOptions);
  React.useEffect(() => {
    function handleResize() {
      if (window.innerWidth <= 800) {
        setIsMobileWidth(true);
      } else {
        setIsMobileWidth(false);
      }
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (
      reduxState.vocabularyContents &&
      reduxState.vocabularyContents.topicLevelContent &&
      Object.keys(reduxState.vocabularyContents.topicLevelContent).length > 0
    ) {
      setTopicLevelWords(reduxState.vocabularyContents.topicLevelContent.wordlist);
    } else {
      setTopicLevelWords([]);
    }
  }, [reduxState.vocabularyContents.topicLevelContent]);

  React.useEffect(() => {
    if (topicLevelWords && topicLevelWords.length > 0) {
      setShowNoWordsMessage(false);
      const framedState: any = [];
      topicLevelWords.forEach((wordDetails: any) => {
        if (wordDetails) {
          framedState.push({
            word: wordDetails.word.replace(/<\/?[^>]+(>|$)/g, ""),
            translation:
              wordDetails.translation === null || wordDetails.translation === ""
                ? ""
                : wordDetails.translation[0]
                  ? wordDetails.translation[0].word.replace(/<\/?[^>]+(>|$)/g, "")
                  : "",
            pos: wordDetails.pos?.replace(/<\/?[^>]+(>|$)/g, ""),
            definition: wordDetails.definition.replace(/<\/?[^>]+(>|$)/g, ""),
            sentence: wordDetails.sentence.replace(/<\/?[^>]+(>|$)/g, ""),
            audioPath: wordDetails.audioPath,
            sentenceAudio: wordDetails.sentenceAudio,
            notes: wordDetails.notes,
            noteId: wordDetails.noteId,
            defId: wordDetails.defId,
            listId: wordDetails.listId,
            posId: wordDetails.posId,
            wordId: wordDetails.wordId,
            wordlist: wordDetails.wordlist,
            isDeletable: isDeletable(wordDetails, reduxState),
          });
        }
      });
      setTableBodyContents(framedState);
    } else {
      setShowNoWordsMessage(true);
      setTableBodyContents([]);
    }
  }, [topicLevelWords]);

  useEffect(() => {
    if (searchText?.length>=2) {
      const filteredListDetails = tableBodyContents?.filter((item: any) =>
        item.word.toLowerCase().includes(searchText.toLowerCase()),
      );
      if (filteredListDetails) {
        // Set the filtered list to filteredTopic
        setFilteredTopic(() => {
          return filteredListDetails;
        });
      } 
       if(filteredListDetails.length === 0&&isUserWordList){
        openAddWordDialog(true);
      }
    } else {
      // If searchText is empty, set filteredTopic to selectedTopic
      setFilteredTopic(tableBodyContents);
    }
  }, [searchText, tableBodyContents]);

  const data = React.useMemo(() => filteredTopic, [filteredTopic]);

  const columns: any = React.useMemo(
    () => [
      {
        id: 1,
        header: translate("vocab_center_word_label"),
        accessorKey: "word",
        cell: (element: any) => (
          <CreateAudioComponent
            audioPath={element.cell.row.original.audioPath}
            value={element.cell.getValue()}
            view={() => setInfoDetails(element.cell.row.original)}
          />
        ),
      },
      {
        id: 2,
        header: translate("vocab_center_translation_label"),
        accessorKey: "translation",
      },
      {
        id: 3,
        header: translate("vocab_center_pos_label"),
        accessorKey: "pos",
      },
      {
        id: 4,
        header: translate("vocab_center_definition_label"),
        accessorKey: "definition",
      },
      {
        id: 5,
        header: translate("vocab_center_sentence_label"),
        accessorKey: "sentence",
        cell: (element: any) => (
          <CreateAudioComponent audioPath={element.cell.row.original.sentenceAudio} value={element.cell.getValue()} />
        ),
      },
    ],
    [options],
  );

  const getOptionsView = () => {
    return (
      <>
        <div className="options-container">
          {options.map((option: any, index) => (
            <div key={getRandomNumber(index)} className="option-filter-type">
              <Checkbox
                data-testid={`${index}check`}
                checked={option.checked}
                onChange={(e: any) => {
                  handleCheckedOptions(e, options, forceUpdate);
                }}
                className="option-filter-checkbox"
                name={option.name}
              />
              <p className="option-label">{option.name}</p>
            </div>
          ))}
        </div>
        <div className="option-filter-button-box">
          <Button variant="contained" className="option-filter-button-text" onClick={() => setShowOptions(false)}>
            {translate("vocab_center_okay_text")}
          </Button>
        </div>
      </>
    );
  };

  const getEditView = () => {
    if (editableWordList.length === 0) {
      setEditableWordList(showingWordListDetails.wordlist);
    }
    return (
      <>
        {getStandardHeaderView(translate("vocab_center_edit_wordlist"), () => {
          setShowEdit(false);
          setEditableWordList("");
        })}
        <div className="edit-view-contents-box">
          <Typography className="edit-view-edit-label">{translate("vocab_center_update_wordlist")}:</Typography>
          <input
            className="word-list-input"
            maxLength={40}
            value={editableWordList}
            onChange={(e) => {
              setShowError(!WORDLIST_REGEX.test(e.target.value));
              setEditableWordList(e.target.value);
              setAlreadyExistsMessage(false);
            }}
          />
        </div>
        {showError && <p className="show-error-message">Please enter a-z,A-Z,0-9,()-.,;:?!&</p>}
        {alreadyExistsMessage && (
          <div className="already-exists-container">
            <AccessTimeFilledIcon className="history-icon" />
            <p className="already-exists-label">{translate("vocab_center_word_exists_in_wordlist_warning_message")}.</p>
          </div>
        )}
        <div className="word-list-action-box">
          <Button
            variant="contained"
            className="word-list-action-button"
            onClick={() => handleWordListActions(showingWordListDetails.listId)}
            disabled={
              alreadyExistsMessage ||
              reduxState.vocabularyContents.isWordListCreating ||
              reduxState.vocabularyContents.isWordListUpdating ||
              editableWordList.length === 0 ||
              showError
            }
          >
            {translate("vocab_center_save_text")}
          </Button>
          <Button
            variant="contained"
            className="word-list-action-button"
            onClick={() => {
              setShowEdit(false);
              setEditableWordList("");
            }}
          >
            {translate("vocab_center_close_text")}
          </Button>
        </div>
      </>
    );
  };

  const getDeleteView = () => {
    return (
      <>
        {getStandardHeaderView(translate("vocab_center_delete_wordlist"), () => {
          setShowDelete(false);
        })}
        <div className="delete-view-contents-box">
          <Typography>{translate("vocab_center_delete_wordlist_warning_message")}</Typography>
          <div className="delete-view-contents-actions">
            <Button variant="contained" className="word-list-action-button" onClick={deleteWordList}>
              {translate("vocab_center_yes_text")}
            </Button>
            <Button variant="contained" className="word-list-action-button" onClick={() => setShowDelete(false)}>
              {translate("vocab_center_close_text")}
            </Button>
          </div>
        </div>
      </>
    );
  };

  const deleteWordList = () => {
    const deletableListId = tableBodyContents[0].listId;
    dispatch(vocabularyDeleteWordList(reduxState.peopleInformation.data.getpeopleInformation.token, deletableListId));
    getVocabularyContentsDetails();
    navigate("/vocabularycenter/userWordlists");
  };

  const getVocabularyContentsDetails = () => {
    const peopleInfoStateValidation = reduxState.peopleInformation ? reduxState.peopleInformation.data : "";
    dispatch(
      getVocabularyContents(
        reduxState.common.lgId,
        peopleInfoStateValidation.getpeopleInformation.token,
        parseInt(peopleInfoStateValidation.getpeopleInformation.accountId, 10),
      ),
    );
  };

  const handleWordListActions = (listId: any) => {
    dispatch(
      vocabularyUpdateWordList(
        reduxState.peopleInformation.data.getpeopleInformation.token,
        listId,
        editableWordList,
        USER,
      ),
    );
    setTimeout(() => {
      getVocabularyContentsDetails();
      setShowEdit(false);
    }, 2000);
  };

  const breadcrumb = () => {
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        <button color="inherit" className="remove-button-default-style" data-testid="Breadcrumbs1" onClick={mainView}>
          Vocabulary Center
        </button>
        <button color="inherit" className="remove-button-default-style" data-testid="Breadcrumbs2" onClick={changeView}>
          {breadcrumbTitle}
        </button>
        <Typography color="textPrimary"> {showingWordListDetails.wordlist}</Typography>
      </Breadcrumbs>
    );
  };

  return reduxState.vocabularyContents.isSubTopicLoading ? (
    <div className="loader-wrapper">
      <CircularProgress color="primary" className="circularLoader" size={100} />
    </div>
  ) : showNoWordsMessage ? (
    <>
      {breadcrumb()}
      <div className="no-data-table-body-contents">
        {!reduxState.vocabularyContents.isLoading && !reduxState.vocabularyContents.isSubTopicLoading && (
          <>
            <p className="no-data-message">{translate("vocab_center_no_word_message")}</p>
            <button className="add-word-button" onClick={() => addWordToList(openAddWordDialog, setAddNewWord)}>
              Add Word
            </button>
          </>
        )}
      </div>
    </>
  ) : !showNoWordsMessage ? (
    <>
      <div className="header-container">
        {breadcrumb()}
        <div className="filter-button-container">
          {!isMobileWidth && (
            <Button className="option-filter-button" data-testid={"optionPopup"} onClick={() => setShowOptions(true)}>
              <TuneIcon /> Options
            </Button>
          )}
          {isUserWordList && (
            <Button className="edit-option-button" onClick={() => setShowEdit(true)}>
              <EditIcon /> Edit
            </Button>
          )}
          {isUserWordList && (
            <Button className="delete-option-button" onClick={() => setShowDelete(true)}>
              <DeleteIcon /> Delete
            </Button>
          )}
        </div>
      </div>
      {showOptions && (
        <DialogView
          open={showOptions}
          dialogContainerClassNameValue="vocabulary-show-options-popup"
          dialogContentsClassName="vocabulary-show-options-popup-contents"
          dialogBody={getOptionsView()}
        />
      )}
      {showEdit && (
        <DialogView
          open={showEdit}
          handleClose={() => setShowEdit(false)}
          dialogContainerClassNameValue="vocabulary-show-edit-popup"
          dialogContentsClassName="vocabulary-show-edit-popup-contents"
          dialogBody={getEditView()}
        />
      )}
      {showDelete && (
        <DialogView
          open={showDelete}
          handleClose={() => setShowDelete(false)}
          dialogContainerClassNameValue="vocabulary-show-delete-popup"
          dialogContentsClassName="vocabulary-show-delete-popup-contents"
          dialogBody={getDeleteView()}
        />
      )}
      {!isMobileWidth && <TableComponent data={data} columns={columns} Visibility={convertToObject(options)} />}
      {isMobileWidth && (
        <div className="react-table-mobile-contents">
          {tableBodyContents.map((bodyContent: any) => (
            <button
              className="remove-button-default-style react-table-each-word"
              key={bodyContent.wordId}
              onClick={() => {
                setInfoDetails(bodyContent);
              }}
            >
              <InfoIcon />
              <p>{bodyContent.word}</p>
            </button>
          ))}
        </div>
      )}
    </>
  ) : (
    <> </>
  );
};

export default ReactTable;
